import { Loader2, Plus, Repeat, ShoppingBag } from 'lucide-react'
import type { AnchorHTMLAttributes, CSSProperties, ReactNode, Ref } from 'react'

import { cn } from '@/lib/cn'

import { price, type Package } from './api'
import { useRevealed, useSpotlight } from './effects'
import { excerpt } from './html'
import { follow, href, packagePath } from './router'
import { useStore } from './state'

/** A link to one of the store's own places. */
export function Link({
  to,
  children,
  className,
  onClick,
  ...props
}: { to: string; children: ReactNode; ref?: Ref<HTMLAnchorElement> } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>) {
  const move = follow(to)
  return (
    <a
      href={href(to)}
      onClick={(event) => {
        onClick?.(event)
        move(event)
      }}
      className={className}
      {...props}
    >
      {children}
    </a>
  )
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        'text-glow inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.28em] uppercase',
        className,
      )}
    >
      <span className="bg-glow/70 h-px w-6" aria-hidden />
      {children}
    </p>
  )
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin', className)} aria-hidden />
}

/** The price a player pays, and what a discount saves them. */
export function Price({ pkg, large = false }: { pkg: Package; large?: boolean }) {
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span
        className={cn(
          'font-display font-semibold tracking-[-0.01em] tabular-nums',
          large ? 'text-[28px]' : 'text-[17px]',
        )}
      >
        {pkg.total_price > 0 ? price(pkg.total_price, pkg.currency) : 'Gratis'}
      </span>
      {pkg.type === 'subscription' && <span className="text-mist text-[12px]">in abbonamento</span>}
      {pkg.discount > 0 && (
        <span className="text-live bg-live/10 rounded-full px-2 py-0.5 text-[11px] font-semibold">
          −{price(pkg.discount, pkg.currency)}
        </span>
      )}
    </span>
  )
}

/** Puts a package in the basket; one with both ways to buy offers the two. */
export function BuyButtons({ pkg, large = false }: { pkg: Package; large?: boolean }) {
  const add = useStore((state) => state.add)
  const busy = useStore((state) => state.busy)
  const adding = busy === `add:${pkg.id}`
  const inBasket = useStore((state) => state.basket?.packages.some((line) => line.id === pkg.id))
  const size = large ? 'h-12 px-6 text-[15px]' : 'h-10 px-4 text-[13px]'

  if (pkg.type === 'both') {
    return (
      <div className={cn('flex flex-wrap gap-2', large && 'gap-3')}>
        <button
          type="button"
          disabled={adding}
          onClick={() => void add(pkg, 'single')}
          className={cn(
            'btn-primary inline-flex items-center gap-2 rounded-full font-semibold',
            size,
          )}
        >
          {adding ? <Spinner /> : <Plus className="size-4" aria-hidden />}
          Una volta
        </button>
        <button
          type="button"
          disabled={adding}
          onClick={() => void add(pkg, 'subscription')}
          className={cn(
            'glass inline-flex items-center gap-2 rounded-full font-medium transition hover:bg-white/10 disabled:opacity-60',
            size,
          )}
        >
          <Repeat className="size-4" aria-hidden />
          Abbonamento
        </button>
      </div>
    )
  }

  return (
    <button
      type="button"
      disabled={adding}
      onClick={() => void add(pkg, pkg.type === 'subscription' ? 'subscription' : 'single')}
      className={cn('btn-primary inline-flex items-center gap-2 rounded-full font-semibold', size)}
    >
      {adding ? (
        <Spinner />
      ) : inBasket ? (
        <ShoppingBag className="size-4" aria-hidden />
      ) : (
        <Plus className="size-4" aria-hidden />
      )}
      {pkg.type === 'subscription' ? 'Abbonati' : inBasket ? 'Aggiungi ancora' : 'Aggiungi'}
    </button>
  )
}

/** A package's picture, or the sword on the night when it has none. */
export function Picture({ pkg, className }: { pkg: Package; className?: string }) {
  return (
    <div
      className={cn(
        'relative overflow-hidden bg-[radial-gradient(120%_90%_at_50%_0%,#1a2a52_0%,#0e1629_55%,#0a1020_100%)]',
        className,
      )}
    >
      {pkg.image ? (
        <img
          src={pkg.image}
          alt=""
          loading="lazy"
          className="absolute inset-0 size-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
        />
      ) : (
        <img src="/brand/unity-mark.png" alt="" className="sword" />
      )}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0a1020] to-transparent"
        aria-hidden
      />
    </div>
  )
}

/** `index` staggers the cards of a row as they rise in. */
export function PackageCard({ pkg, index = 0 }: { pkg: Package; index?: number }) {
  const words = excerpt(pkg.description)
  const card = useSpotlight<HTMLElement>({ tilt: true })
  const [ref, shown] = useRevealed<HTMLDivElement>()
  return (
    <div
      ref={ref}
      data-shown={shown}
      className="reveal h-full"
      style={{ '--delay': `${(index % 3) * 90}ms` } as CSSProperties}
    >
      <article
        ref={card}
        className="card spotlight tilt group flex h-full flex-col overflow-hidden transition-colors hover:border-white/15"
      >
        <Link to={packagePath(pkg)} className="block" aria-label={pkg.name}>
          <Picture pkg={pkg} className="aspect-[16/10]" />
        </Link>
        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-display text-[16px] leading-snug font-semibold tracking-[-0.01em]">
            <Link to={packagePath(pkg)} className="hover:text-glow transition-colors">
              {pkg.name}
            </Link>
          </h3>
          {words && (
            <p className="text-mist mt-2 line-clamp-2 text-[13.5px] leading-relaxed">{words}</p>
          )}
          <div className="mt-auto flex flex-col items-start gap-4 pt-5">
            <Price pkg={pkg} />
            <BuyButtons pkg={pkg} />
          </div>
        </div>
      </article>
    </div>
  )
}

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <h2 className="font-display text-[20px] font-semibold tracking-[-0.01em] text-white sm:text-[22px]">
        {children}
      </h2>
      {aside}
    </div>
  )
}
